import { useApiClient } from '~/composables/api/api'
import { z } from 'zod'
import type { RiskAssessmentResponse } from '~/clients'
import type { MutationOptions } from '@tanstack/vue-query'

export const riskAssessmentSchema = z.object({
  code: z.string().min(1).max(8),
  nameAsJson: zodTranslation('Name is required'),
  descriptionAsJson: zodTranslation('', true),
  remarksAsJson: zodTranslation('', true),
  riskAssessmentId: z.number().optional(),
})

export type RiskAssessmentForm = z.infer<typeof riskAssessmentSchema>
export const useCreateRiskAssessment = (
  mutationOptions?: MutationOptions<RiskAssessmentResponse, Error, RiskAssessmentForm>,
) => {
  const localePath = useLocalePath()
  const { getApiErrorMessage } = useApiError()
  const toast = useToast()
  const { mutate: update, isPending: updating } = useMutation({
    mutationFn: async (body: RiskAssessmentForm) => {
      return await useApiClient<RiskAssessmentResponse>(
        `risk-assessments/${body.riskAssessmentId}/copy`,
        {
          method: 'PATCH',
          body,
        },
      )
    },
    ...mutationOptions,
    onSuccess(data) {
      navigateTo(localePath(`/hub/risk-assessments/${data.id}`))
    },
    onError(error) {
      const message = getApiErrorMessage(error?.data, 'An error occured')
      toast.error(message)
    },
  })

  const { mutate: create, isPending: creating } = useMutation({
    mutationFn: async (body: RiskAssessmentForm) => {
      return await useApiClient<RiskAssessmentResponse>('/risk-assessments', {
        method: 'POST',
        body,
      })
    },
    onSuccess(data) {
      navigateTo(localePath(`/hub/risk-assessments/${data.id}`))
    },
    onError(error) {
      const message = getApiErrorMessage(error?.data, 'An error occured')
      toast.error(message)
    },
    ...mutationOptions,
  })

  return {
    update,
    create,
    creating,
    updating,
  }
}
